import { memo } from 'react';
import PropTypes from 'prop-types';
import Badge from './Badge.jsx';

const ICON_COLOR_CLASSES = {
  blue: 'text-[#2173FF] dark:text-[#80AFFF]',
  purple: 'text-[#B070F5] dark:text-purple-300',
  amber: 'text-[#FFCC00] dark:text-amber-300',
  green: 'text-[#009978] dark:text-emerald-300',
  red: 'text-[#D32F2F] dark:text-red-300',
  neutral: 'text-content-muted',
};

const CONNECTOR_VARIANT_CLASSES = {
  default: 'bg-[#BABABA] dark:bg-gray-500/55',
  subtle: 'bg-[#D9D9D9] dark:bg-gray-600/45',
};

/** Keep in sync with `Badge.variantClasses` in Badge.jsx. */
const SCOPE_COLOR_OPTIONS = [
  'default',
  'primary',
  'classicPrimary',
  'success',
  'warning',
  'danger',
  'secondary',
  'neutral',
  'informative',
  'error',
  'alert',
  'limeGreen',
  'yellow',
  'purple',
  'lightBlue',
];

const cn = (...parts) => parts.filter(Boolean).join(' ');

/**
 * Compact vertical timeline matching the process item treatment from Figma:
 * 16px icon rail, thin connector, title/description/date and a small scope badge.
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.title]
 * @param {Array<{
 *   id: string|number,
 *   title: import('react').ReactNode,
 *   description?: import('react').ReactNode,
 *   icon?: import('lucide-react').LucideIcon,
 *   iconColor?: 'blue'|'purple'|'amber'|'green'|'red'|'neutral',
 *   scope?: import('react').ReactNode,
 *   scopeColor?: 'default'|'primary'|'classicPrimary'|'success'|'warning'|'danger'|'secondary'|'neutral'|'informative'|'error'|'alert'|'limeGreen'|'yellow'|'purple'|'lightBlue',
 *   date?: import('react').ReactNode,
 * }>} [props.items]
 * @param {boolean} [props.showTrailingConnector=true]
 * @param {'default'|'subtle'} [props.connectorVariant='default']
 * @param {string} [props.className]
 */
const TimeLine = ({
  title,
  items = [],
  showTrailingConnector = true,
  connectorVariant = 'default',
  className = '',
}) => {
  const list = Array.isArray(items) ? items : [];
  const connectorClass =
    CONNECTOR_VARIANT_CLASSES[connectorVariant] ?? CONNECTOR_VARIANT_CLASSES.default;

  return (
    <section className={cn('w-full min-w-0', className)}>
      {title ? (
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.48px] text-content dark:text-gray-100">
          {title}
        </h3>
      ) : null}

      <ul className="flex flex-col gap-2">
        {list.map((item, index) => {
          const Icon = item.icon ?? null;
          const iconClassName =
            ICON_COLOR_CLASSES[item.iconColor] ?? ICON_COLOR_CLASSES.neutral;
          const showConnector = showTrailingConnector || index < list.length - 1;

          return (
            <li key={item.id} className="grid grid-cols-[16px_minmax(0,1fr)] gap-4">
              <div className="flex min-h-full flex-col items-center self-stretch">
                <span
                  className={cn('flex size-4 shrink-0 items-center justify-center', iconClassName)}
                  aria-hidden
                >
                  {Icon ? <Icon className="size-4" strokeWidth={2} /> : null}
                </span>
                {showConnector ? (
                  <span className={cn('w-px flex-1', connectorClass)} aria-hidden />
                ) : null}
              </div>

              <div className="min-w-0 pb-1">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 text-sm font-semibold leading-none text-content dark:text-gray-100">
                    {item.title}
                  </div>
                  {item.scope ? (
                    <Badge size="small" variant={item.scopeColor ?? 'default'} className="shrink-0">
                      {item.scope}
                    </Badge>
                  ) : null}
                </div>

                {item.description ? (
                  <div className="mt-1 text-sm font-medium leading-5 text-content-muted dark:text-gray-300">
                    {item.description}
                  </div>
                ) : null}

                {item.date ? (
                  <div className="mt-0.5 text-[10px] font-medium leading-none text-content-muted dark:text-gray-400">
                    {item.date}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

TimeLine.propTypes = {
  title: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.node.isRequired,
      description: PropTypes.node,
      icon: PropTypes.elementType,
      iconColor: PropTypes.oneOf(['blue', 'purple', 'amber', 'green', 'red', 'neutral']),
      scope: PropTypes.node,
      scopeColor: PropTypes.oneOf(SCOPE_COLOR_OPTIONS),
      date: PropTypes.node,
    }),
  ),
  showTrailingConnector: PropTypes.bool,
  connectorVariant: PropTypes.oneOf(['default', 'subtle']),
  className: PropTypes.string,
};

const MemoTimeLine = memo(TimeLine);
MemoTimeLine.displayName = 'TimeLine';

export default MemoTimeLine;
